<template>
  <div class="archive pa-4">
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon left class="mr-2">mdi-archive</v-icon>
              {{ $t('archive.title') }}
            </div>
            <v-btn
              color="primary"
              variant="text"
              prepend-icon="mdi-arrow-left"
              @click="goToDashboard"
            >
              {{ $t('archive.backToDashboard') }}
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="archivedProjects"
              :loading="loading"
              :items-per-page="pageSize"
              :page="currentPage"
              :server-items-length="totalProjects"
              @update:page="onPageChange"
              class="elevation-1"
              :loading-text="$t('common.loading')"
              :no-data-text="$t('archive.noProjects')"
            >
              <!-- Project Type Column -->
              <template v-slot:[`item.projectType`]="{ item }">
                <v-chip size="small" :color="getProjectTypeColor(item.projectType)">
                  {{ getProjectTypeLabel(item.projectType) }}
                </v-chip>
              </template>

              <!-- Client Column -->
              <template v-slot:[`item.client`]="{ item }">
                <span class="font-weight-medium">{{ item.client || '-' }}</span>
              </template>

              <!-- Status Column -->
              <template v-slot:[`item.activeStatus`]="{ item }">
                <v-chip 
                  size="small" 
                  :color="getStatusColor(item.projectType, item.activeStatus)"
                >
                  {{ getStatusLabel(item.projectType, item.activeStatus) }}
                </v-chip>
              </template>

              <!-- Status Change Date Column -->
              <template v-slot:[`item.statusChangeDate`]="{ item }">
                {{ formatDate(item.statusChangeDate) }}
              </template>

              <!-- ID Project Column -->
              <template v-slot:[`item.idProject`]="{ item }">
                <span class="text-caption font-weight-bold">{{ item.idProject }}</span>
              </template>

              <!-- Actions Column -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-folder-open"
                      size="small"
                      variant="text"
                      @click="openArchivedFolder(item)"
                    ></v-btn>
                  </template>
                  <span>{{ $t('archive.openFolder') }}</span>
                </v-tooltip>
                
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-eye"
                      size="small"
                      variant="text"
                      @click="viewProject(item)"
                    ></v-btn>
                  </template>
                  <span>{{ $t('archive.viewDetails') }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Project Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px" scrollable>
      <v-card v-if="selectedProject">
        <v-card-title class="d-flex justify-space-between align-center">
          <div>
            <v-icon class="mr-2">mdi-archive</v-icon>
            {{ selectedProject.idProject }}
          </div>
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog = false"></v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4" style="max-height: 600px;">
          <v-row>
            <!-- Basic Info -->
            <v-col cols="12" md="6">
              <h3 class="text-h6 mb-3">{{ $t('archive.basicInfo') }}</h3>
              
              <v-list density="compact">
                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.fields.projectType') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    <v-chip size="small" :color="getProjectTypeColor(selectedProject.projectType)">
                      {{ getProjectTypeLabel(selectedProject.projectType) }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.fields.client') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1 font-weight-medium">
                    {{ selectedProject.client || '-' }}
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.fields.activeStatus') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    <v-chip size="small" :color="getStatusColor(selectedProject.projectType, selectedProject.activeStatus)">
                      {{ getStatusLabel(selectedProject.projectType, selectedProject.activeStatus) }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item>
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.fields.statusChangeDate') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    {{ formatDate(selectedProject.statusChangeDate) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>

            <!-- Additional Fields -->
            <v-col cols="12" md="6">
              <h3 class="text-h6 mb-3">{{ $t('archive.additionalInfo') }}</h3>
              
              <v-list density="compact">
                <v-list-item v-if="selectedProject.reference">
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.fields.reference') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    {{ selectedProject.reference }}
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item v-if="selectedProject.projectDescription">
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.fields.projectDescription') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    {{ selectedProject.projectDescription }}
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item v-if="selectedProject.createdAt">
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('projects.table.createdAt') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    {{ formatDateTime(selectedProject.createdAt) }}
                  </v-list-item-subtitle>
                </v-list-item>

                <v-list-item v-if="selectedProject.updatedAt">
                  <v-list-item-title class="text-caption text-grey">
                    {{ $t('archive.lastUpdate') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 mt-1">
                    {{ formatDateTime(selectedProject.updatedAt) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>

            <!-- All Fields -->
            <v-col cols="12" v-if="Object.keys(selectedProject).length > 0">
              <v-divider class="my-4"></v-divider>
              <h3 class="text-h6 mb-3">{{ $t('archive.allFields') }}</h3>
              
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-title>
                    {{ $t('archive.viewAllFields') }}
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-list density="compact">
                      <v-list-item v-for="(value, key) in selectedProject" :key="key">
                        <v-list-item-title class="text-caption text-grey">
                          {{ key }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-body-2 mt-1">
                          {{ formatValue(value) }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="detailsDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { apiCall } from '../utils/api.js'

const { t } = useI18n()
const projectStore = useProjectStore()
const localBackendStore = useLocalBackendStore()
const router = useRouter()

// State
const archivedProjects = ref([])
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(50)
const totalProjects = ref(0)
const detailsDialog = ref(false)
const selectedProject = ref(null)

// Navigation
function goToDashboard() {
  router.push({ name: 'dashboard' })
}

// Table headers
const headers = computed(() => [
  { title: t('projects.table.id'), key: 'idProject', sortable: false, width: '200px' },
  { title: t('projects.fields.projectType'), key: 'projectType', sortable: false, width: '120px' },
  { title: t('projects.fields.client'), key: 'client', sortable: false },
  { title: t('projects.fields.activeStatus'), key: 'activeStatus', sortable: false, width: '150px' },
  { title: t('projects.fields.statusChangeDate'), key: 'statusChangeDate', sortable: false, width: '150px' },
  { title: t('projects.table.actions'), key: 'actions', sortable: false, width: '120px', align: 'center' }
])

// Load archived projects
async function loadArchivedProjects(page = 1) {
  try {
    loading.value = true
    const endpoint = `/api/projects/archived?page=${page}&pageSize=${pageSize.value}`
    const data = await apiCall(endpoint)
    
    archivedProjects.value = data.projects
    totalProjects.value = data.total
    currentPage.value = data.page
  } catch (error) {
    console.error('Error loading archived projects:', error)
  } finally {
    loading.value = false
  }
}

// Page change handler
function onPageChange(page) {
  loadArchivedProjects(page)
}

// View project details
function viewProject(project) {
  selectedProject.value = project
  detailsDialog.value = true
}

// Open archived folder
async function openArchivedFolder(project) {
  if (!localBackendStore.isConnected) {
    alert(t('home.messages.localBackendNotRunning'))
    return
  }

  try {
    await projectStore.openArchivedProjectFolder(project.idProject)
  } catch (error) {
    console.error('Error opening archived folder:', error)
    alert(t('archive.folderError'))
  }
}

// Get project type label
function getProjectTypeLabel(typeId) {
  const type = projectStore.projectTypes.find(pt => pt.id.toString() === typeId?.toString())
  return type?.descr || typeId
}

// Get project type color
function getProjectTypeColor(typeId) {
  const type = projectStore.projectTypes.find(pt => pt.id.toString() === typeId?.toString())
  return type?.color || 'grey'
}

// Get status label
function getStatusLabel(projectType, statusId) {
  const type = projectStore.projectTypes.find(pt => pt.id.toString() === projectType?.toString())
  const status = type?.statusList?.find(s => s.id.toString() === statusId?.toString())
  return status?.descr || statusId
}

// Get status color
function getStatusColor(projectType, statusId) {
  const type = projectStore.projectTypes.find(pt => pt.id.toString() === projectType?.toString())
  const status = type?.statusList?.find(s => s.id.toString() === statusId?.toString())
  return status?.color || 'grey'
}

// Format date
function formatDate(date) {
  if (!date) return '-'
  try {
    // Se la data è in formato YYYY-MM-DD, parsala come data locale
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number)
      const d = new Date(year, month - 1, day) // month è 0-indexed
      return d.toLocaleDateString('it-IT')
    }
    const d = new Date(date)
    return d.toLocaleDateString('it-IT')
  } catch {
    return date
  }
}

// Format datetime
function formatDateTime(datetime) {
  if (!datetime) return '-'
  try {
    const d = new Date(datetime)
    return d.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return datetime
  }
}

// Format value
function formatValue(value) {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return value.toString()
}

// Load on mount
onMounted(async () => {
  await projectStore.loadConfigurations()
  await loadArchivedProjects()
})
</script>

<style scoped>
.archive {
  max-width: 1400px;
  margin: 0 auto;
}
</style>
